import {
  ADD_DETAILS,
  DONATIONS,
  FIXED_PERCENTAGE,
  FIXED_VALUE,
  ITEM_BASED,
  ADD_RETURN_REASONS,
  MAX_WEIGHT,
} from "../core-modules/constants";
import React, { useEffect, useState } from "react";

import BodyText from "./BodyText";
import Box from "./Box";
import Divider from "./Divider";
import FormError from "./FormError";
import HeaderRow from "./HeaderRow";
import ItemRow from "./ItemRow";
import Loading from "./Loading";
import LozengeButton from "./LozengeButton";
import PropTypes from "prop-types";
import SelectLarge from "./SelectLarge";
import SpanText from "./SpanText";
import TabButton from "./TabButton";
import Text from "./Text";
import { getCategories } from "../core-modules/utils";
import { getTakebackValue } from "../core-modules/utils";
import { useAppContext } from "./AppContext";
import { useBrand } from "./BrandContext";
import { useTakebackConfig } from "./TakebackConfigContext";

function TakebackForm({ instore }) {
  const {
    donatedTakebackItems,
    takebackItems,
    itemFilter,
    setCurrentStep,
    setItemFilter,
    incrementTakebackItem,
    decrementTakebackItem,
    addTakebackItem,
    removeTakebackItemsById,
    addDonatedTakebackItem,
    removeDonatedTakebackItemsById,
    incrementDonatedTakebackItem,
    decrementDonatedTakebackItem,
  } = useAppContext();

  const { donationItems } = useTakebackConfig();
  const brand = useBrand();
  const categories = getCategories(brand);
  const [maxWeightError, setMaxWeightError] = useState(false);

  useEffect(() => {
    if (itemFilter === null) {
      setItemFilter(categories[0]);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setMaxWeightError(false);
  }, [takebackItems, donatedTakebackItems]);

  const filteredItems = brand.items.filter((item) => {
    return item.category.name === itemFilter;
  });

  if (itemFilter === null) return <Loading />;

  // Create the form tabs/dropdown
  const categoriesWithCount = categories.map((category) => {
    // The number of items in basket that fall under this category
    const itemsCategoryCount = takebackItems.filter(
      (item) => item.category.name === category
    ).length;
    let verboseName = category;
    if (itemsCategoryCount > 0) {
      verboseName = `${category} (${itemsCategoryCount})`;
    }
    return { value: category, verboseName: verboseName };
  });

  // If this brand accepts donations then we need to add that option to
  // the tabs/dropdown

  if (donationItems) {
    let verboseName = DONATIONS;
    if (donatedTakebackItems.length > 0) {
      verboseName = `${DONATIONS} (${donatedTakebackItems.length})`;
    }
    categoriesWithCount.push({ value: DONATIONS, verboseName: verboseName });
  }

  // Type of items rendered and basket methods will differ based on whether the
  // current filter is donations, so set up these vars here
  let items;
  let incrementItem;
  let decrementItem;
  let addItem;
  let removeItemsById;
  let itemsInBasket;

  // Check for not only the current tab being 'Donations' but also whether
  // or not this brand actually accepts donations - this avoids conflict
  // where a 'Donations' category has been manually created
  if (itemFilter === DONATIONS && donationItems) {
    items = donationItems;
    itemsInBasket = donatedTakebackItems;
    incrementItem = incrementDonatedTakebackItem;
    decrementItem = decrementDonatedTakebackItem;
    addItem = addDonatedTakebackItem;
    removeItemsById = removeDonatedTakebackItemsById;
  } else {
    items = filteredItems;
    itemsInBasket = takebackItems;
    incrementItem = incrementTakebackItem;
    decrementItem = decrementTakebackItem;
    addItem = addTakebackItem;
    removeItemsById = removeTakebackItemsById;
  }

  const showValueColumn = brand.incentiveType === ITEM_BASED;

  const minRequirementsNotMet =
    (brand.incentiveType === FIXED_VALUE ||
      brand.incentiveType === FIXED_PERCENTAGE) &&
    brand.minItems &&
    brand.minItems - takebackItems.length > 0;

  const weight = [...takebackItems, ...donatedTakebackItems].reduce(
    (acc, obj) => acc + obj.weight,
    0
  );

  return (
    <>
      <Box pt={40}>
        {categoriesWithCount.length > 1 && (
          <>
            <Box display={["none", "flex", null, null]}>
              {categoriesWithCount.map((category) => {
                return (
                  <Box mr="2px" key={category.value}>
                    <TabButton
                      active={category.value === itemFilter}
                      onClick={() => {
                        setItemFilter(category.value);
                      }}
                    >
                      {category.verboseName}
                    </TabButton>
                  </Box>
                );
              })}
            </Box>
            <Box display={[null, "none", null, null]}>
              <SelectLarge
                options={categoriesWithCount}
                value={itemFilter}
                handleChange={(value) => {
                  setItemFilter(value);
                }}
              />
            </Box>
          </>
        )}

        <Box display={["none", "block", null, null]}>
          <Divider />
        </Box>
        <Box>
          <HeaderRow colNo={showValueColumn ? 3 : 2} />
          {items.map((item, index) => {
            const quantity = itemsInBasket.filter(
              (itemInBasket) => itemInBasket === item
            ).length;
            return (
              <ItemRow
                key={index}
                item={item}
                quantity={quantity}
                incrementItem={incrementItem}
                decrementItem={decrementItem}
                addItem={addItem}
                removeItemsById={removeItemsById}
                colNo={showValueColumn ? 3 : 2}
              />
            );
          })}
          <Box
            py={[25, 40, null, null]}
            backgroundColor="background"
            display="flex"
            flexDirection={["column", "row", null, null]}
            justifyContent="flex-end"
            alignItems={["center", "left", null, null]}
          >
            <Box>
              <Text
                fontSize={[22, 32, 32, 32]}
                pr={[null, 38, null, null]}
                textAlign="center"
              >
                {brand.incentiveType === FIXED_PERCENTAGE
                  ? `${brand.name} discount: `
                  : "Takeback value: "}
                <SpanText fontWeight={700} fontFamily="bold">
                  {getTakebackValue(brand, takebackItems)}
                </SpanText>
              </Text>
              {brand.incentiveType === ITEM_BASED && brand.maxDiscount && (
                <BodyText pt="15px" textAlign={["center", "left", null, null]}>
                  (Maximum takeback value £{brand.maxDiscount})
                </BodyText>
              )}
              {minRequirementsNotMet && (
                <BodyText pt="15px" textAlign={["center", "left", null, null]}>
                  (Add {brand.minItems - takebackItems.length} more item
                  {brand.minItems - takebackItems.length > 1 ? "s" : ""} to
                  qualify)
                </BodyText>
              )}
            </Box>
            {typeof instore === "undefined" && (
              <Box position="relative" pt={[25, 0, null, null]}>
                <LozengeButton
                  disabled={
                    getTakebackValue(brand, takebackItems) === "£0" ||
                    getTakebackValue(brand, takebackItems) === "0%" ||
                    maxWeightError
                  }
                  onClick={() => {
                    if (weight > MAX_WEIGHT) {
                      setMaxWeightError(true);
                    } else {
                      if (
                        takebackItems.filter((item) => item.category.getReasons)
                          .length > 0
                      ) {
                        setCurrentStep(ADD_RETURN_REASONS);
                      } else {
                        setCurrentStep(ADD_DETAILS);
                      }
                    }
                  }}
                >
                  Submit your takeback
                </LozengeButton>
                {maxWeightError && (
                  <FormError name="weight" positionProps={{ bottom: -55 }}>
                    This takeback is over the maximum 15kg weight limit, please
                    remove some items and resubmit
                  </FormError>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

TakebackForm.propTypes = {
  instore: PropTypes.bool,
};

export default TakebackForm;
